import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { ActivatedRouteSnapshot, Resolve, RouterStateSnapshot } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { TransferState, makeStateKey } from '@angular/platform-browser';
import { isPlatformServer } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class ServiceResolver implements Resolve<any> {
  constructor(
    private http: HttpClient,
    private transferState: TransferState,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  private useTransferState<T>(key: string, observable$: Observable<T>): Observable<T> {
    const STATE_KEY = makeStateKey<T>(key);

    if (this.transferState.hasKey(STATE_KEY)) {
      const cached = this.transferState.get<T>(STATE_KEY, null as any);
      return of(cached);
    }

    return observable$.pipe(
      map(data => {
        if (isPlatformServer(this.platformId)) {
          this.transferState.set(STATE_KEY, data);
        }
        return data;
      })
    );
  }

  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<any> {
    const lang = route.url[0]?.path || 'en';

    // ✅ Absolute asset paths for SSR compatibility
    const mainDataUrl = `/assets/${environment.projectName}/pages-components.json`;
    const metadataUrl = `/assets/${environment.projectName}/content/${lang}/service-page/meta-data.json`;

    return forkJoin({
      mainData: this.useTransferState(
        `maindata-${lang}`,
        this.http.get<any>(mainDataUrl).pipe(catchError(() => of({})))
      ),
      metadata: this.useTransferState(
        `metadata-${lang}`,
        this.http.get<any>(metadataUrl).pipe(catchError(() => of({})))
      )
    }).pipe(
      switchMap(({ mainData, metadata }) => {
        const serviceComponents = mainData?.serviceComponents || [];

        if (serviceComponents.length > 0) {
          const requests = serviceComponents.map((item: any) => {
            const fileName = item.fileName;
            const subJsonPath = `/assets/${environment.projectName}/content/${lang}/service-page/${fileName}.json`;

            return this.useTransferState(
              `subJson-${lang}-${fileName}`,
              this.http.get(subJsonPath).pipe(
                map(details => ({ ...item, details })),
                catchError(error => {
                  console.error(`Failed to fetch ${fileName}:`, error);
                  return of({ ...item, details: null });
                })
              )
            );
          });

          return forkJoin(requests).pipe(
            map(componentsWithDetails => ({
              ...(mainData || {}),
              serviceComponents: componentsWithDetails,
              metadata
            }))
          );
        } else {
          console.warn('No components found in serviceComponents.');
          return of({
            ...(mainData || {}),
            metadata
          });
        }
      }),
      catchError(error => {
        console.error('Error fetching service page data:', error);
        return of(null);
      })
    );
  }
}
